import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { variable } from '~/prix/types/presets/variable'
import legalEntityStatsAction from './legalEntityStats.action.json' with { type: 'json' }
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { latitude, longitude } from '~/prix/types/presets/geo'
import { dateOrIsoString } from '~/prix/types/date'
import { getZodSchemaInfo } from '~/prix/types/zodType'

export const legalEntityShape = z.object({
  originalId: z.string().describe('ID original'),
  dataSourceId: z.number().nullish().describe('ID da fonte de dados'),
  tradeName: z.string().nullish().describe('Nome fantasia'),
  corporateName: z.string().nullish().describe('Razão social'),
  cnpj: z.string().describe('CNPJ'),
  openingDate: dateOrIsoString().nullish().describe('Data de abertura'),
  mainCnae: z.string().optional().describe('CNAE principal'),
  latitude: latitude().nullish().describe('Latitude'),
  longitude: longitude().nullish().describe('Longitude'),

  city: z.string().nullish().describe('Cidade'),
  state: z.string().describe('Estado'),
  neighborhood: z.string().nullish().describe('Bairro'),
  street: z.string().nullish().describe('Rua'),
  registrationStatus: z.string().nullish().describe('Situação cadastral'),
  registrationStatusReason: z.string().nullish().describe('Motivo da situação cadastral'),
  size: z.string().nullish().describe('Porte'),
  isSimples: z.boolean().nullish().describe('É optante pelo Simples Nacional'),
  isMei: z.boolean().nullish().describe('É MEI'),
  capital: z.number().nullish().describe('Capital social'),
  email: z.string().nullish().describe('E-mail'),
  phoneNumber1AreaCode: z.string().nullish().describe('DDD 1'),
  phoneNumber1: z.string().nullish().describe('Telefone 1'),
  phoneNumber2AreaCode: z.string().nullish().describe('DDD 2'),
  phoneNumber2: z.string().nullish().describe('Telefone 2'),
})
export const legalEntityExpression = expression(legalEntityShape.shape)

export const legalEntityVariable: Variable = {
  key: 'legalEntity',
  actionDefinitionKey: legalEntityStatsAction.key,
  title: 'Empresas e estabelecimentos',
  categoryKey: 'economyLegalEntities',
  unitSingular: 'estabelecimento',
  unitPlural: 'estabelecimentos',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(legalEntityShape),
  betterDensity: 'less',
  basicSelect: ['corporateName', 'tradeName'],
  detailedSelect: [
    'openingDate',
    'longitude',
    'latitude',
    'city',
    'state',
    'neighborhood',
    'street',
    'registrationStatus',
    'registrationStatusReason',
    'size',
    'isSimples',
    'isMei',
    'capital',
    'email',
    'phoneNumber1AreaCode',
    'phoneNumber1',
    'phoneNumber2AreaCode',
    'phoneNumber2',
  ],
  assertions: prepareAssertions(legalEntityExpression),
  compatibleChildrenTypes: ['item'],
}

const legalEntityStatsDefinition = defineAction({
  ...legalEntityStatsAction,
  input: z.object({
    variable,
    highlight: legalEntityExpression.optional(),
    filter: legalEntityExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(legalEntityShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (input.filter || input.space?.delimitationType === 'custom') {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
    }
  },
})

export default legalEntityStatsDefinition
